import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { IconButton } from '@/ui/primitives';
import { SearchPalette } from './SearchPalette';

/**
 * The launcher, at both widths, plus the shortcut that opens it.
 *
 * It renders three siblings because the shell's header row is a flex line and
 * the control has to occupy different space at each width:
 *
 *  - DESKTOP: a `flex-1` box between the brand and the icon cluster, where the
 *    bare spacer used to be. It takes the space that was already empty; the
 *    bell, theme toggle, profile link and sign-out keep their positions and
 *    their widths exactly.
 *  - PHONE: the spacer stays a spacer, and search is a single `IconButton`
 *    immediately before the bell. That makes three visible controls at 360px —
 *    brand, search, bell — inside the budget the shell's own comment sets at
 *    five, and it costs ONE tap. Not a sixth bottom tab (the bar is at its
 *    five-cell capacity) and not a row inside the Menü sheet as well: two entry
 *    points for one feature is how they drift apart.
 *
 * Keeping all of it here is what holds `AppShell` to a single hunk.
 */
export function SearchTrigger() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Not on a phone: a shortcut there fights the on-screen keyboard, and the
      // literal media query is what every other handler in this app uses.
      if (window.matchMedia('(max-width: 767px)').matches) return;
      // Stand down while any dialog is open — `Modal` sets role="dialog" and
      // owns its own Escape, and a palette opened on top of a sheet makes one
      // Escape close the wrong layer. This is `QueueView.tsx:149`'s guard.
      if (document.querySelector('[role="dialog"]')) return;
      if (!((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k')) return;
      // Browsers bind Cmd/Ctrl+K to the address bar.
      event.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /*
   * Ctrl/Cmd+K rather than `/`: `/` already focuses `#dst-q` on /destek and `n`
   * opens create there (SupportPage's shortcut effect, Destek tab only). ⌘K
   * collides with nothing in this codebase. The "bail while typing" rule those
   * handlers use is deliberately NOT copied — it is for bare letters, and a
   * modifier chord that dies inside a text field is a shortcut that never fires.
   */
  return (
    <>
      <div className="hidden flex-1 justify-center px-2 md:flex">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Genel arama"
          title="Ara (Ctrl+K)"
          // `CONTROL` in primitives.tsx is module-local and not exported, so the
          // control vocabulary is inlined rather than re-declared as a second
          // shared constant nobody would keep in step.
          className="border-border bg-bg text-fg-muted hover:border-primary flex h-9 w-full max-w-sm items-center gap-2 rounded-lg border px-3 text-sm transition"
        >
          <Search size={15} className="shrink-0" />
          <span>Ara</span>
          <kbd className="border-border ml-auto hidden rounded border px-1 font-sans text-[10px] lg:inline">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="flex-1 md:hidden" />

      <IconButton
        onClick={() => setOpen(true)}
        aria-label="Genel arama"
        title="Ara"
        className="md:hidden"
      >
        <Search size={18} />
      </IconButton>

      <SearchPalette open={open} onClose={() => setOpen(false)} />
    </>
  );
}
